import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config';
import Toolbar from './FolderExplorer/Toolbar';
import FileTable from './FolderExplorer/FileTable';
import ContextMenu from './FolderExplorer/ContextMenu';
import VersionControl from './VersionControl';
import UploadFilesMigracion from './UploadFilesMigracion';
import ImageLightbox from './FolderExplorer/ImageLightbox';
import './FolderExplorer/FolderExplorer.css';
import { isPdfsEntregaPath } from '../utils/folderPaths';

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];

const isImageFile = (filename) => {
  const ext = filename.split('.').pop()?.toLowerCase();
  return IMAGE_EXTENSIONS.includes(ext);
};

const buildFileUrl = (file, currentPath, expedienteFolderName) => {
  const filePath = currentPath ? `${currentPath}/${file.name}` : file.name;
  const cacheBust = file.lastModified ? new Date(file.lastModified).getTime() : Date.now();
  return `${config.uploadsUrl}/${expedienteFolderName}/${filePath}?v=${cacheBust}`;
};

const FolderExplorer = ({
  expedienteId,
  expedienteNumero,
  onFileClick,
  onDownloadFile,
  initialFolderHint = '',
}) => {
  const { showError, showSuccess, showConfirm } = useToast();
  const { user } = useAuth();
  const puedeModoMigracion = Boolean(user);

  // Estados principales
  const [currentPath, setCurrentPath] = useState('');
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expedienteFolderName, setExpedienteFolderName] = useState('');
  
  // Estados de selección
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState(null);
  
  // Estados de vista y navegación
  const [viewMode, setViewMode] = useState('details'); // 'details' o 'grid'
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estados de clipboard (cortar/copiar/pegar)
  const [clipboard, setClipboard] = useState({
    items: [],
    operation: null // 'copy' o 'cut'
  });
  
  // Estados de historial de navegación
  const [history, setHistory] = useState(['']);
  const [historyIndex, setHistoryIndex] = useState(0);
  
  // Estados de menú contextual
  const [contextMenu, setContextMenu] = useState(null);
  
  // Estados de versiones
  const [showVersionsModal, setShowVersionsModal] = useState(false);
  const [selectedFileForVersions, setSelectedFileForVersions] = useState(null);

  // Estados de subida de archivos
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [initialFolderApplied, setInitialFolderApplied] = useState(false);
  
  // Estados de descarga ZIP
  const [downloadProgress, setDownloadProgress] = useState(null); // { folderName, progress, loaded, total }

  // Visor de fotos integrado
  const [imageViewer, setImageViewer] = useState(null); // { images: [{name, url}], index: number }
  
  // Referencias
  const containerRef = useRef(null);
  
  // Cargar contenido de carpeta
  const loadFolderContent = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setSelectedItems(new Set());
      
      const response = await fetch(
        `${config.apiUrl}/folders/expediente/${expedienteId}?path=${encodeURIComponent(currentPath)}&_=${Date.now()}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          cache: 'no-store'
        }
      );

      if (!response.ok) {
        throw new Error('Error al cargar la estructura de carpetas');
      }

      const data = await response.json();
      setFolders(data.folders || []);
      
      // Cargar archivos de BD
      try {
        const archivosResponse = await fetch(
          `${config.apiUrl}/archivos/expediente/${expedienteId}?_=${Date.now()}`,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            cache: 'no-store'
          }
        );
        
        if (archivosResponse.ok) {
          const archivosData = await archivosResponse.json();
          const archivosDB = archivosData.archivos || [];
          
          const archivosEnCarpeta = archivosDB.filter(archivo => {
            const archivoPath = archivo.ruta.split('/');
            const currentPathParts = currentPath ? currentPath.split('/') : [];
            return archivoPath.length > currentPathParts.length && 
                   currentPathParts.every((part, index) => archivoPath[index] === part);
          });
          
          const processedFiles = (data.files || []).map(file => {
            const archivoDB = archivosEnCarpeta.find(db => 
              db.nombreOriginal === file.name || 
              db.ruta.endsWith(file.name)
            );
            
            return {
              ...file,
              id: archivoDB?.id,
              version: archivoDB?.version || 1,
              archivoId: archivoDB?.id
            };
          });
          
          setFiles(processedFiles);
        } else {
          setFiles(data.files || []);
        }
      } catch (dbError) {
        console.warn('Error cargando archivos de BD:', dbError);
        setFiles(data.files || []);
      }
      
      setExpedienteFolderName(data.expedienteFolderName || '');
      
    } catch (error) {
      console.error('Error cargando contenido:', error);
      setError('Error al cargar el contenido de la carpeta');
    } finally {
      setLoading(false);
    }
  }, [expedienteId, currentPath]);

  // Combinar carpetas y archivos
  useEffect(() => {
    let items = [
      ...folders.map(folder => ({
        ...folder,
        id: `folder-${folder.path || folder.name}`,
        type: 'folder',
        isFolder: true,
        size: '-',
        date: null
      })),
      ...files.map(file => ({
        ...file,
        id: `file-${file.name}`,
        type: 'file',
        isFolder: false
      }))
    ];
    
    // Filtrar por búsqueda
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      items = items.filter(item => 
        item.name.toLowerCase().includes(term)
      );
    }
    
    // Ordenar
    items = ordenarItems(items, sortBy, sortOrder);
    setAllItems(items);
  }, [folders, files, sortBy, sortOrder, searchTerm]);

  useEffect(() => {
    loadFolderContent();
  }, [loadFolderContent]);

  useEffect(() => {
    if (initialFolderApplied || !initialFolderHint || currentPath) return;
    if (!Array.isArray(folders) || folders.length === 0) return;

    // Misma lógica que createExpedienteFolders: espacios → _; comparar sin espacios ni guiones.
    const folderMatchKey = (s) =>
      String(s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');
    const hint = folderMatchKey(initialFolderHint);
    const matched = folders.find((f) => folderMatchKey(f.name) === hint)
      || folders.find((f) => {
        const key = folderMatchKey(f.name);
        return key.includes(hint) || hint.includes(key);
      });

    if (matched) {
      setCurrentPath(matched.name);
      setSearchTerm('');
    } else {
      setSearchTerm(initialFolderHint);
    }
    setInitialFolderApplied(true);
  }, [folders, currentPath, initialFolderHint, initialFolderApplied]);

  // Función de ordenamiento
  const ordenarItems = (items, sortBy, order) => {
    const sorted = [...items].sort((a, b) => {
      if (a.isFolder && !b.isFolder) return -1;
      if (!a.isFolder && b.isFolder) return 1;
      
      let comparison = 0;
      
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name, 'es', { numeric: true, sensitivity: 'base' });
          break;
        case 'size':
          const sizeA = a.isFolder ? 0 : parseSize(a.size);
          const sizeB = b.isFolder ? 0 : parseSize(b.size);
          comparison = sizeA - sizeB;
          break;
        case 'date':
          const dateA = a.lastModified ? new Date(a.lastModified).getTime() : 0;
          const dateB = b.lastModified ? new Date(b.lastModified).getTime() : 0;
          comparison = dateA - dateB;
          break;
        case 'type':
          const typeA = a.isFolder ? 'folder' : getFileExtension(a.name);
          const typeB = b.isFolder ? 'folder' : getFileExtension(b.name);
          comparison = typeA.localeCompare(typeB);
          break;
        default:
          comparison = 0;
      }
      
      return order === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  };

  const parseSize = (sizeStr) => {
    if (!sizeStr || sizeStr === '-') return 0;
    const match = sizeStr.match(/([\d.]+)\s*(Bytes|KB|MB|GB)/i);
    if (!match) return 0;
    const value = parseFloat(match[1]);
    const unit = match[2].toUpperCase();
    const multipliers = { BYTES: 1, KB: 1024, MB: 1024 ** 2, GB: 1024 ** 3 };
    return value * (multipliers[unit] || 1);
  };

  const getFileExtension = (fileName) => {
    return fileName.split('.').pop()?.toLowerCase() || '';
  };

  // Navegación
  const navigateTo = (path) => {
    setCurrentPath(path);
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(path);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const goBack = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setCurrentPath(history[historyIndex - 1]);
    }
  };

  const goForward = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setCurrentPath(history[historyIndex + 1]);
    }
  };

  const goUp = () => {
    if (currentPath) {
      const parts = currentPath.split('/');
      parts.pop();
      navigateTo(parts.join('/'));
    }
  };

  // Selección
  const toggleSelection = (itemId, event) => {
    const newSelected = new Set(selectedItems);
    
    if (event?.ctrlKey) {
      // Ctrl+Click: toggle individual
      if (newSelected.has(itemId)) {
        newSelected.delete(itemId);
      } else {
        newSelected.add(itemId);
      }
    } else if (event?.shiftKey && lastSelectedIndex !== null) {
      // Shift+Click: selección de rango
      const currentIndex = allItems.findIndex(item => item.id === itemId);
      const start = Math.min(lastSelectedIndex, currentIndex);
      const end = Math.max(lastSelectedIndex, currentIndex);
      
      for (let i = start; i <= end; i++) {
        newSelected.add(allItems[i].id);
      }
    } else {
      // Click normal: solo este item
      newSelected.clear();
      newSelected.add(itemId);
    }
    
    setSelectedItems(newSelected);
    setLastSelectedIndex(allItems.findIndex(item => item.id === itemId));
  };

  const selectAll = () => {
    if (selectedItems.size === allItems.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(allItems.map(item => item.id)));
    }
  };

  // Toggle solo este ítem (checkbox o celda): añade/quita de selección sin borrar el resto
  const handleCheckboxAreaClick = (itemId) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
    } else {
      newSelected.add(itemId);
    }
    setSelectedItems(newSelected);
    setLastSelectedIndex(allItems.findIndex(item => item.id === itemId));
  };

  // Clipboard (cortar/copiar/pegar)
  const handleCopy = () => {
    const itemsToCopy = allItems.filter(item => selectedItems.has(item.id));
    setClipboard({
      items: itemsToCopy,
      operation: 'copy',
      sourcePath: currentPath
    });
    showSuccess(`${itemsToCopy.length} elemento(s) copiado(s)`);
  };

  const handleCut = () => {
    const itemsToCut = allItems.filter(item => selectedItems.has(item.id));
    setClipboard({
      items: itemsToCut,
      operation: 'cut',
      sourcePath: currentPath
    });
    showSuccess(`${itemsToCut.length} elemento(s) cortado(s)`);
  };

  const handlePaste = async () => {
    if (!clipboard.items.length) {
      showError('No hay elementos en el portapapeles');
      return;
    }

    try {
      for (const item of clipboard.items) {
        const sourcePath = clipboard.sourcePath ? `${clipboard.sourcePath}/${item.name}` : item.name;
        const destPath = currentPath ? `${currentPath}/${item.name}` : item.name;
        
        const endpoint = clipboard.operation === 'cut' 
          ? `${config.apiUrl}/folders/expediente/${expedienteId}/move`
          : `${config.apiUrl}/folders/expediente/${expedienteId}/copy`;
        
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            sourcePath,
            destPath,
            isFolder: item.isFolder
          })
        });

        if (!response.ok) {
          throw new Error(`Error al ${clipboard.operation === 'cut' ? 'mover' : 'copiar'} ${item.name}`);
        }
      }

      showSuccess(`${clipboard.items.length} elemento(s) ${clipboard.operation === 'cut' ? 'movido(s)' : 'copiado(s)'} correctamente`);
      
      if (clipboard.operation === 'cut') {
        setClipboard({ items: [], operation: null, sourcePath: '' });
      }
      
      loadFolderContent();
    } catch (error) {
      showError(error.message);
    }
  };

  // Atajos de teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+A: Seleccionar todo
      if (e.ctrlKey && e.key === 'a' && !e.target.matches('input, textarea')) {
        e.preventDefault();
        selectAll();
      }
      
      // Ctrl+C: Copiar
      if (e.ctrlKey && e.key === 'c' && !e.target.matches('input, textarea') && selectedItems.size > 0) {
        e.preventDefault();
        handleCopy();
      }
      
      // Ctrl+X: Cortar
      if (e.ctrlKey && e.key === 'x' && !e.target.matches('input, textarea') && selectedItems.size > 0) {
        e.preventDefault();
        handleCut();
      }
      
      // Ctrl+V: Pegar
      if (e.ctrlKey && e.key === 'v' && !e.target.matches('input, textarea') && clipboard.items.length > 0) {
        e.preventDefault();
        handlePaste();
      }
      
      // Delete: Eliminar
      if (e.key === 'Delete' && !e.target.matches('input, textarea') && selectedItems.size > 0) {
        e.preventDefault();
        handleDeleteSelected();
      }
      
      // Escape: Deseleccionar
      if (e.key === 'Escape') {
        setSelectedItems(new Set());
        setContextMenu(null);
      }
      
      // Ctrl+F: Buscar
      if (e.ctrlKey && e.key === 'f' && !e.target.matches('input, textarea')) {
        e.preventDefault();
        const searchInput = document.querySelector('.toolbar-search input');
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedItems, clipboard, allItems]);

  // Eliminar seleccionados (backend espera fileName + folderPath en query)
  const handleDeleteSelected = async () => {
    const itemsToDelete = allItems.filter(item => selectedItems.has(item.id));
    
    if (!await showConfirm(`¿Eliminar ${itemsToDelete.length} elemento(s)?`)) {
      return;
    }

    try {
      for (const item of itemsToDelete) {
        const fileName = item.name;
        const folderPath = currentPath || '';
        
        const qs = new URLSearchParams();
        qs.set('fileName', fileName);
        if (folderPath) qs.set('folderPath', folderPath);
        qs.set('isFolder', item.isFolder ? '1' : '0');
        
        const url = `${config.apiUrl}/folders/expediente/${expedienteId}/file?${qs.toString()}`;
        const response = await fetch(url, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (!response.ok) {
          const errBody = await response.json().catch(() => ({}));
          const msg = errBody?.error || `Error al eliminar ${item.name}`;
          throw new Error(msg);
        }
      }

      showSuccess(`${itemsToDelete.length} elemento(s) eliminado(s)`);
      setSelectedItems(new Set());
      loadFolderContent();
    } catch (error) {
      showError(error.message);
    }
  };

  const openImageViewer = (item) => {
    const imagesInFolder = allItems.filter((i) => !i.isFolder && isImageFile(i.name));
    const index = imagesInFolder.findIndex((i) => i.id === item.id);
    if (index < 0 || !expedienteFolderName) return;

    setImageViewer({
      images: imagesInFolder.map((img) => ({
        name: img.name,
        url: buildFileUrl(img, currentPath, expedienteFolderName),
      })),
      index,
    });
  };

  // Abrir carpeta o archivo
  const handleItemDoubleClick = (item) => {
    if (item.isFolder) {
      const newPath = currentPath ? `${currentPath}/${item.name}` : item.name;
      navigateTo(newPath);
    } else {
      const itemWithPath = {
        ...item,
        fullPath: currentPath ? `${currentPath}/${item.name}` : item.name,
      };
      if (isImageFile(item.name)) {
        openImageViewer(item);
      } else if (onFileClick) {
        onFileClick(itemWithPath);
      }
    }
  };

  // Menú contextual
  const handleContextMenu = (item, event) => {
    event.preventDefault();
    setContextMenu({
      x: event.clientX,
      y: event.clientY,
      item,
      selectedCount: selectedItems.size
    });
  };

  // Subida de archivos
  const handleUploadComplete = () => {
    setShowUploadModal(false);
    loadFolderContent();
  };

  // Descargar carpeta como ZIP con barra de progreso
  const handleDownloadFolder = async (folder) => {
    try {
      const folderPath = currentPath ? `${currentPath}/${folder.name}` : folder.name;
      const url = `${config.apiUrl}/folders/expediente/${expedienteId}/download-folder?path=${encodeURIComponent(folderPath)}`;
      
      // Inicializar progreso (comprimiendo)
      setDownloadProgress({
        folderName: folder.name,
        progress: 0,
        loaded: 0,
        total: 0,
        status: 'compressing' // Estado: comprimiendo
      });
      
      // Crear AbortController con timeout muy largo (10 minutos)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 minutos
      
      try {
        // Descargar usando fetch con seguimiento de progreso
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (!response.ok) {
          throw new Error('Error al descargar la carpeta');
        }

        // Obtener el tamaño total del archivo
        const contentLength = response.headers.get('content-length');
        const total = parseInt(contentLength, 10);

        // Cambiar estado a descargando
        setDownloadProgress({
          folderName: folder.name,
          progress: 0,
          loaded: 0,
          total,
          status: 'downloading' // Estado: descargando
        });

        // Leer el stream con progreso
        const reader = response.body.getReader();
        const chunks = [];
        let loaded = 0;

        while (true) {
          const { done, value } = await reader.read();
          
          if (done) break;
          
          chunks.push(value);
          loaded += value.length;
          
          // Actualizar progreso
          const progress = total ? Math.round((loaded / total) * 100) : 0;
          setDownloadProgress({
            folderName: folder.name,
            progress,
            loaded,
            total,
            status: 'downloading'
          });
        }

        // Crear blob desde los chunks
        const blob = new Blob(chunks);
        
        // Crear URL y descargar
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `${folder.name}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
        
        // Limpiar progreso después de un momento
        setTimeout(() => setDownloadProgress(null), 2000);
        
        showSuccess(`Carpeta "${folder.name}" descargada exitosamente`);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        
        if (fetchError.name === 'AbortError') {
          throw new Error('La descarga tardó demasiado tiempo (timeout de 10 minutos)');
        }
        throw fetchError;
      }
    } catch (error) {
      console.error('Error descargando carpeta:', error);
      setDownloadProgress(null);
      showError('Error al descargar la carpeta como ZIP: ' + error.message);
    }
  };

  const handleDownloadSelected = async () => {
    const selectedFiles = allItems.filter((item) => selectedItems.has(item.id) && !item.isFolder);
    if (selectedFiles.length === 0) {
      showError('Selecciona al menos un archivo para descargar');
      return;
    }

    if (selectedFiles.length === 1) {
      const file = selectedFiles[0];
      onDownloadFile?.({
        ...file,
        fullPath: currentPath ? `${currentPath}/${file.name}` : file.name,
      });
      return;
    }

    try {
      setDownloadProgress({
        folderName: `${selectedFiles.length} archivos`,
        progress: 0,
        loaded: 0,
        total: 0,
        status: 'compressing'
      });

      const response = await fetch(`${config.apiUrl}/folders/expediente/${expedienteId}/download-files`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          path: currentPath,
          files: selectedFiles.map((file) => file.name)
        })
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Error al descargar los archivos seleccionados');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${expedienteNumero || expedienteId}-archivos.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      setDownloadProgress(null);
      showSuccess(`${selectedFiles.length} archivos descargados como ZIP`);
    } catch (error) {
      console.error('Error descargando archivos seleccionados:', error);
      setDownloadProgress(null);
      showError(error.message || 'Error al descargar los archivos seleccionados');
    }
  };

  const esCarpetaLibreEntrega = isPdfsEntregaPath(currentPath);
  const puedeGestionarCarpetaActual = puedeModoMigracion || esCarpetaLibreEntrega;

  if (loading) {
    return (
      <div className="folder-explorer-loading">
        <div className="spinner-border" style={{ width: '3rem', height: '3rem', color: '#4F46E5' }}>
          <span className="sr-only">Cargando...</span>
        </div>
        <p className="mt-3">Cargando archivos...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning m-3" role="alert">
        <strong>No se pudo cargar el explorador de archivos.</strong>
        <p className="mb-2 mt-2 small">{error}</p>
        <button type="button" className="btn btn-sm btn-primary" onClick={loadFolderContent}>
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="folder-explorer-windows" ref={containerRef}>
      <Toolbar
        currentPath={currentPath}
        expedienteFolderName={expedienteFolderName}
        canGoBack={historyIndex > 0}
        canGoForward={historyIndex < history.length - 1}
        canGoUp={currentPath !== ''}
        selectedCount={selectedItems.size}
        viewMode={viewMode}
        searchTerm={searchTerm}
        clipboard={clipboard}
        onGoBack={goBack}
        onGoForward={goForward}
        onGoUp={goUp}
        onNavigateTo={navigateTo}
        onToggleView={() => setViewMode(viewMode === 'details' ? 'grid' : 'details')}
        onSearch={setSearchTerm}
        onDelete={handleDeleteSelected}
        onCopy={handleCopy}
        onCut={handleCut}
        onPaste={handlePaste}
        onRefresh={loadFolderContent}
        onUpload={puedeGestionarCarpetaActual ? () => setShowUploadModal(true) : undefined}
      />

      <div className="explorer-main">
        <div className="explorer-content explorer-content-full">
          <FileTable
            items={allItems}
            selectedItems={selectedItems}
            viewMode={viewMode}
            sortBy={sortBy}
            sortOrder={sortOrder}
            clipboardItems={clipboard.items}
            clipboardOperation={clipboard.operation}
            onToggleSelection={toggleSelection}
            onCheckboxAreaClick={handleCheckboxAreaClick}
            onSelectAll={selectAll}
            onSort={(column) => {
              if (sortBy === column) {
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
              } else {
                setSortBy(column);
                setSortOrder('asc');
              }
            }}
            onItemDoubleClick={handleItemDoubleClick}
            onContextMenu={handleContextMenu}
          />

          {contextMenu && (
            <ContextMenu
              x={contextMenu.x}
              y={contextMenu.y}
              item={contextMenu.item}
              selectedCount={contextMenu.selectedCount}
              clipboard={clipboard}
              onClose={() => setContextMenu(null)}
              onCopy={handleCopy}
              onCut={handleCut}
              onPaste={handlePaste}
              onDelete={handleDeleteSelected}
              onUpload={puedeGestionarCarpetaActual ? () => {
                setShowUploadModal(true);
                setContextMenu(null);
              } : undefined}
              onDownloadFolder={(folder) => {
                handleDownloadFolder(folder);
                setContextMenu(null);
              }}
              onDownloadFile={onDownloadFile ? (file) => {
                const fileWithPath = {
                  ...file,
                  fullPath: currentPath ? `${currentPath}/${file.name}` : file.name
                };
                onDownloadFile(fileWithPath);
                setContextMenu(null);
              } : null}
              onDownloadSelected={() => {
                handleDownloadSelected();
                setContextMenu(null);
              }}
              onVersions={(file) => {
                setSelectedFileForVersions(file);
                setShowVersionsModal(true);
                setContextMenu(null);
              }}
            />
          )}
        </div>
      </div>

      {showVersionsModal && selectedFileForVersions && (
        <VersionControl
          archivoId={selectedFileForVersions.archivoId}
          onClose={() => {
            setShowVersionsModal(false);
            setSelectedFileForVersions(null);
          }}
          onVersionCreated={loadFolderContent}
        />
      )}

      {puedeGestionarCarpetaActual && showUploadModal && (
        <div className="upload-modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="upload-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="upload-modal-header">
              <h3>Subir Archivos</h3>
              <button 
                className="upload-modal-close" 
                onClick={() => setShowUploadModal(false)}
                title="Cerrar"
              >
                ×
              </button>
            </div>
            <UploadFilesMigracion
              expedienteId={expedienteId}
              currentPath={currentPath}
              onUploadComplete={handleUploadComplete}
            />
          </div>
        </div>
      )}

      {imageViewer && (
        <ImageLightbox
          images={imageViewer.images}
          currentIndex={imageViewer.index}
          onClose={() => setImageViewer(null)}
          onNavigate={(index) => setImageViewer((prev) => (prev ? { ...prev, index } : null))}
        />
      )}

      {/* Barra de progreso de descarga ZIP */}
      {downloadProgress && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          minWidth: '320px',
          zIndex: 1000,
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            marginBottom: '12px',
            gap: '10px'
          }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#17a2b8" strokeWidth="2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
            <div style={{ flex: 1 }}>
              <div style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#2c3e50',
                marginBottom: '4px'
              }}>
                {downloadProgress.status === 'compressing' ? 'Comprimiendo carpeta...' : 'Descargando ZIP'}
              </div>
              <div style={{
                fontSize: '12px',
                color: '#5a6c7d',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {downloadProgress.folderName}
              </div>
            </div>
          </div>
          
          {/* Barra de progreso */}
          <div style={{
            width: '100%',
            height: '8px',
            backgroundColor: '#e0e0e0',
            borderRadius: '4px',
            overflow: 'hidden',
            marginBottom: '8px'
          }}>
            <div style={{
              width: `${downloadProgress.progress}%`,
              height: '100%',
              backgroundColor: '#17a2b8',
              transition: 'width 0.3s ease',
              borderRadius: '4px'
            }}></div>
          </div>
          
          {/* Información de progreso */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            fontSize: '11px',
            color: '#7f8c8d'
          }}>
            <span>
              {downloadProgress.status === 'compressing' 
                ? 'Preparando archivo...' 
                : `${downloadProgress.progress}%`
              }
            </span>
            <span>
              {downloadProgress.status === 'downloading' && downloadProgress.total > 0 
                ? `${(downloadProgress.loaded / 1024 / 1024).toFixed(2)} MB / ${(downloadProgress.total / 1024 / 1024).toFixed(2)} MB`
                : downloadProgress.status === 'compressing'
                ? 'Comprimiendo...'
                : `${(downloadProgress.loaded / 1024 / 1024).toFixed(2)} MB`
              }
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FolderExplorer;
